<template>
  <div class="p-field">
    <div>
      <label v-if="label" :for="id" class="label-text flex justify-between" :class="{ 'p-error': isInvalid }">
        <div class="flex">
          <p>{{ label }}</p>
          <span v-if="required" class="text-red-500">*</span>
        </div>


        <slot name="adddata"></slot>
      </label>
    </div>
    <div>
      <MultiSelect ref="inputRef" :id="id" v-model="fieldValue" :options="options" :optionLabel="optionLabel"
        :optionValue="optionValue" :optionDisabled="optionDisabled" :optionGroupLabel="optionGroupLabel"
        :optionGroupChildren="optionGroupChildren" :placeholder="placeholder" :filter="filter"
        :filterPlaceholder="filterPlaceholder" :filterLocale="filterLocale" :filterMatchMode="filterMatchMode"
        :filterFields="filterFields" :disabled="disabled" :dataKey="dataKey" :emptyFilterMessage="emptyFilterMessage"
        :emptyMessage="emptyMessage" :display="display" :panelStyle="panelStyle" :panelClass="panelClass"
        :loading="loading" :loadingIcon="loadingIcon" :maxSelectedLabels="maxSelectedLabels"
        :selectionLimit="selectionLimit" :showToggleAll="showToggleAll" :showHeader="showHeader" :showClear="showClear"
        :virtualScrollerOptions="virtualScrollerOptions" :class="[
          'one-multiselect',
          {
            'p-invalid': isInvalid,
            'p-disabled': disabled
          }
        ]" :aria-label="props['aria-label']" :aria-labelledby="props['aria-labelledby']" :aria-invalid="isInvalid"
        :aria-required="required" :aria-describedby="ariaDescribedBy" :pt="{
          root: { class: wrapperClass }
        }" @change="handleChange" @filter="onFilter" @before-show="$emit('before-show')"
        @before-hide="$emit('before-hide')" @show="$emit('show')" @hide="onHide"
        @select="(e: any) => $emit('select', e)" @unselect="(e: any) => $emit('unselect', e)"
        @selectAll="(e: any) => $emit('selectAll', e)" @unselectAll="(e: any) => $emit('unselectAll', e)">
        <template v-if="$slots.value" #value="slotProps">
          <slot name="value" v-bind="slotProps"></slot>
        </template>

        <template v-if="$slots.option" #option="slotProps">
          <slot name="option" v-bind="slotProps"></slot>
        </template>

        <template v-if="$slots.optiongroup" #optiongroup="slotProps">
          <slot name="optiongroup" v-bind="slotProps"></slot>
        </template>

        <template v-if="$slots.header" #header="slotProps">
          <slot name="header" v-bind="slotProps"></slot>
        </template>

        <template v-if="$slots.footer" #footer="slotProps">
          <slot name="footer" v-bind="slotProps"></slot>
        </template>

        <template v-if="$slots.emptyfilter" #emptyfilter>
          <slot name="emptyfilter"></slot>
        </template>

        <template v-if="$slots.empty" #empty>
          <slot name="empty"></slot>
        </template>

        <template v-if="$slots.loader" #loader>
          <slot name="loader"></slot>
        </template>

        <template v-if="$slots.removetokenicon" #removetokenicon>
          <slot name="removetokenicon"></slot>
        </template>
      </MultiSelect>
    </div>

    <!-- Error Messages -->
    <div v-if="showErrorMessages" class="p-error-messages">
      <small v-if="helperText" :id="helperTextId" class="p-hint" :class="{ 'p-error': isInvalid }">{{ helperText
      }}</small>

      <!-- Show field-specific error message -->
      <small v-if="shouldShowError || (props.required && isEmpty(fieldValue))" class="p-error block text-red-500">
        {{ props.name ? `${props.name} is required` : (errorMessage || getRequiredErrorMessage) }}
      </small>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import MultiSelect from 'primevue/multiselect'

export default defineComponent({
  name: 'ONEMultiSelect',
  components: {
    MultiSelect
  },
  inheritAttrs: false
})
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useFormContext } from '../../../../composables/useFormContext'
import { useLocalization } from '../../../../composables/useLocalization'
import { useVeeValidate } from '../../../../composables/useVeeValidate'
import type { ONEMultiSelectProps } from './types'

const props = withDefaults(defineProps<ONEMultiSelectProps>(), {
  modelValue: undefined,
  options: () => [],
  optionLabel: undefined,
  optionValue: undefined,
  optionDisabled: undefined,
  optionGroupLabel: undefined,
  optionGroupChildren: undefined,
  placeholder: undefined,
  filter: false,
  filterPlaceholder: undefined,
  filterLocale: undefined,
  filterMatchMode: 'contains',
  filterFields: () => [],
  disabled: false,
  dataKey: undefined,
  emptyFilterMessage: 'No results found',
  emptyMessage: 'No available options',
  display: 'comma',
  panelStyle: undefined,
  panelClass: undefined,
  loading: false,
  loadingIcon: 'pi pi-spinner pi-spin',
  maxSelectedLabels: undefined,
  selectionLimit: undefined,
  showToggleAll: true,
  showHeader: true,
  showClear: false,
  virtualScrollerOptions: undefined,
  label: undefined,
  helperText: '',
  invalid: false,
  required: false,
  id: () => `one-multiselect-${Math.random().toString(36).substr(2, 9)}`,
  'aria-label': '',
  'aria-labelledby': '',
  validationRules: () => [],
  validateOnBlur: true,
  validateOnInput: false,
  validateOnChange: true,
  showErrors: true,
  name: undefined,
  formContext: true,
  wrapperClass: undefined
})

const emit = defineEmits([
  'update:modelValue',
  'change',
  'filter',
  'before-show',
  'before-hide',
  'show',
  'hide',
  'select',
  'unselect',
  'selectAll',
  'unselectAll',
  'validation-error',
  'validation-success'
])

// Form Context and Localization
const { formState } = useFormContext()
const { t } = useLocalization()

// Setup VeeValidate
const {
  fieldValue,
  errorMessage,
  shouldShowError,
  validate,
  handleValidation,
  updateValue,
  isEmpty
} = useVeeValidate({
  // Ensure we always have a name for validation messages
  name: props.name || 'Field',
  modelValue: props.modelValue,
  required: props.required,
  validationType: undefined,
  customRules: undefined
})

// Initialize fieldValue with modelValue
fieldValue.value = props.modelValue

// Local State
const inputRef = ref<any>(null)
const isDirty = ref(false)
const isFocused = ref(false)

// Computed properties
const isInvalid = computed(() => shouldShowError.value || props.invalid)

// Map errors from form context if available
const currentErrors = computed(() => {
  if (errorMessage.value) {
    return [errorMessage.value]
  }
  if (formState && props.name) {
    return formState.errors.value[props.name] || []
  }
  return []
})

const localizedErrors = computed(() => {
  return currentErrors.value.map(error => {
    if (error.startsWith('validation.')) {
      return t(error)
    }
    if (error.toLowerCase().includes('required')) {
      return t('validation.required')
    }
    return error
  })
})

const helperTextId = computed(() => `${props.id}-helper`)

const ariaDescribedBy = computed(() => {
  const ids = []
  if (props.helperText) ids.push(helperTextId.value)
  currentErrors.value.forEach((_, index) => ids.push(`${props.id}-error-${index}`))
  return ids.join(' ')
})

const showErrorMessages = computed(() => {
  return props.showErrors && (
    !!props.helperText ||
    shouldShowError.value ||
    currentErrors.value.length > 0
  )
})

const getRequiredErrorMessage = computed(() => {
  // Always use the name prop for the error message
  return `${props.name || 'Field'} is required`
})

// The getRequiredErrorMessage is already defined above

const handleChange = async (event: { originalEvent: Event; value: any }) => {
  updateValue(event.value)
  emit('update:modelValue', event.value)
  emit('change', event)
  isDirty.value = true

  if (shouldShowError.value) {
    await validate()
  }
}

const onFilter = (event: { originalEvent: Event; value: string }) => {
  emit('filter', event)
}

// No longer needed as we're using $emit directly

const onHide = async () => {
  emit('hide')
  isFocused.value = false
  isDirty.value = true

  if (formState && props.name) {
    formState.setFieldTouched(props.name, true)
  }

  await handleValidation()
}

// Watch for modelValue changes
watch(
  () => props.modelValue,
  (newVal) => {
    fieldValue.value = newVal;

    if (formState && props.name) {
      formState.setFieldValue(props.name, newVal)
    }

    if (shouldShowError.value) {
      validate()
    }
  }
);

// Watch for external validation triggers
watch(() => props.invalid, (newValue) => {
  if (newValue) {
    isDirty.value = true
  }
})

// Initialize form context
onMounted(() => {
  if (formState && props.name && props.formContext) {
    if (props.validationRules.length > 0) {
      formState.addValidation(props.name, props.validationRules)
    }

    // Set initial value in form state
    formState.setFieldValue(props.name, props.modelValue)
  }
})

// Expose methods for parent form validation
defineExpose({
  validate,
  errorMessage,
  isValid: computed(() => !isInvalid.value)
})
</script>
